import { Component, OnInit, ViewChild } from "@angular/core";
import {
  ExperienceStepModel,
  GeneralExpWizardInfoByLang,
  ScheduleExperience,
} from "@models/experience-steps.model";
import { Observable, ReplaySubject } from "rxjs";
import { ActivatedRoute, Router } from "@angular/router";
import {
  ExperienceStepsService,
  LANGUAGE,
} from "src/app/services/experience/experience-steps.service";
import { ExperiencesService } from "src/app/services/experience/experiences.service";
import { takeUntil } from "rxjs/operators";
import { ErrorEmitterService } from "src/app/services/emitters/error-emitter.service";
import { TimeService } from "src/app/services/generic/time.service";
import { NgbDateParserFormatter } from "@ng-bootstrap/ng-bootstrap";
import { Globals } from "src/app/common/globals";
import { TranslateService } from "@ngx-translate/core";
import { OidcSecurityService } from "angular-auth-oidc-client";

@Component({
  selector: "app-experiences-steps",
  templateUrl: "./experiences-steps.component.html",
  styleUrls: ["./experiences-steps.component.scss"],
})
export class ExperiencesStepsComponent implements OnInit {
  private destroyed$: ReplaySubject<boolean> = new ReplaySubject(1);

  currentStep: Observable<ExperienceStepModel>;
  generalInfoByLang: GeneralExpWizardInfoByLang;
  experienceId: string;

  public categories: Array<any> = Globals.categories;

  public experienceOrigin: boolean;
  public experienceOriginUrl: string;

  public comments: Array<any> = [];
  public commentInfo: boolean;
  public commentSchedule: boolean;
  public commentLocation: boolean;
  public commentImmage: boolean;
  public hasIssue: boolean = false;
  public hasComments: boolean = false;

  public loader: boolean = false;

  @ViewChild("generalInformationStep", { static: false })
  generalInformationStep;

  constructor(
    public auth: OidcSecurityService,
    private stepsService: ExperienceStepsService,
    private router: Router,
    private route: ActivatedRoute,
    private experienceSvc: ExperiencesService,
    private errorEmitter: ErrorEmitterService,
    private dateFormatter: NgbDateParserFormatter,
    private timeSvc: TimeService,
    private translate: TranslateService
  ) {}

  ngOnInit(): void {
    this.experienceId = this.route.snapshot.params.experienceId;

    this.stepsService.clearService();

    this.currentStep = this.stepsService.getCurrentStep();
    this.generalInfoByLang = this.stepsService.getGeneralInfo();

    this.experienceSvc
      .getExperienceVersionDetails(this.experienceId)
      .pipe(takeUntil(this.destroyed$))
      .subscribe(
        (x) => {
          // Set General information
          this.setBasicInfo(x.result.experienceVersionInfos);

          // Set Location & address
          this.setLocationInfo(x.result.location);

          // Set Images
          this.setImages(x.result.coverImagePath, x.result.galleryImagePath);

          // Set schedules
          this.setSchedules(x.result.schedules);

          // Set type
          this.setType(x.result.type);

          // Set origin
          if (x.result.origin) {
            this.experienceOrigin = true;
            this.experienceOriginUrl = x.result.origin.url;
          } else {
            this.experienceOrigin = false;
          }

          // Set comments
          if (x.result.hasIssue == true) {
            this.hasComments = true;
            this.comments = x.result.issues;
            for (let x = 0, len = this.comments.length; x < len; x++) {
              if (this.comments[x].type == "INFO") {
                this.commentInfo = true;
              }
              if (this.comments[x].type == "SCHEDULE") {
                this.commentSchedule = true;
              }
              if (this.comments[x].type == "IMAGES") {
                this.commentImmage = true;
              }
              if (this.comments[x].type == "LOCATION") {
                this.commentLocation = true;
              }
            }
          }

          this.generalInformationStep.checkStepComplete();
        },
        (err) => {
          this.errorEmitter.sendError(true);
        }
      );
  }

  publishForApproval() {
    this.loader = true;
    this.experienceSvc
      .publishForApproval(this.experienceId)
      .pipe(takeUntil(this.destroyed$))
      .subscribe(
        async (x) => {
          this.loader = false;
          const profile = await this.auth.getUserData().toPromise();
          const isTrusted = profile['https://strowr.com/isTrusted']
          if (isTrusted) {
            this.router.navigate(["active"]);
          } else {
            this.router.navigate(["pendingApproval"]);
          }
        },
        (err) => {
          this.errorEmitter.sendError(true);
        }
      );
  }

  setBasicInfo(basicInfo: any) {
    if (basicInfo.length != 0) {
      var tempLangs = [];

      Object.keys(LANGUAGE).forEach((key) => {
        let tempLangIndex = basicInfo.findIndex(
          (item) => item.languageTag === LANGUAGE[key]
        );

        if (tempLangIndex != -1) {
          tempLangs.push(LANGUAGE[key]);

          this.generalInfoByLang.dataByLang[LANGUAGE[key]].title =
            basicInfo[tempLangIndex].title;
          this.generalInfoByLang.dataByLang[LANGUAGE[key]].description =
            basicInfo[tempLangIndex].description;
        }
      });

      if (tempLangs.length === Object.keys(LANGUAGE).length) {
        this.generalInfoByLang.language = this.translate.currentLang;
      } else {
        this.generalInfoByLang.language = tempLangs[0];
      }
    }
  }

  setLocationInfo(location: any) {
    Object.keys(LANGUAGE).forEach((key) => {
      let tempLangIndex = location.locationInfos.findIndex(
        (item) => item.languageTag === LANGUAGE[key]
      );

      if (
        location.coordinates.latitude != 0 ||
        location.coordinates.longitude != 0
      ) {
        this.generalInfoByLang.dataByLang[LANGUAGE[key]].latitude =
          location.coordinates.latitude;
        this.generalInfoByLang.dataByLang[LANGUAGE[key]].longitude =
          location.coordinates.longitude;
      }

      if (tempLangIndex != -1) {
        this.generalInfoByLang.dataByLang[LANGUAGE[key]].locationAddress =
          location.locationInfos[tempLangIndex].addressText;
        this.generalInfoByLang.dataByLang[LANGUAGE[key]].locationDescription =
          location.locationInfos[tempLangIndex].description;
        this.generalInfoByLang.dataByLang[LANGUAGE[key]].city =
          location.locationInfos[tempLangIndex].city;
        this.generalInfoByLang.dataByLang[LANGUAGE[key]].country =
          location.locationInfos[tempLangIndex].country;
        this.generalInfoByLang.dataByLang[LANGUAGE[key]].locationName =
          location.locationInfos[tempLangIndex].addressName;
      }
    });
  }

  setImages(coverImage: any, gallery: any) {
    if (gallery.length != 0) {
      this.generalInfoByLang.loadedImagesArray = gallery;
    }

    if (coverImage) {
      this.generalInfoByLang.selectedCoverImage = coverImage.original;
      this.generalInfoByLang.activeCoverImage = coverImage.original;
    }
  }

  setSchedules(schedules: any) {
    if (schedules.length != 0) {
      for (let i = 0, len = schedules.length; i < len; i++) {
        var startDateAsLocalISOTime = this.ConvertDateStringToLocalDateStringIso(
          schedules[i].startDate
        );
        var endDateAsLocalISOTime = this.ConvertDateStringToLocalDateStringIso(
          schedules[i].endDate
        );

        let startDateParse = this.dateFormatter.parse(startDateAsLocalISOTime);
        let endDateParse = this.dateFormatter.parse(endDateAsLocalISOTime);
        let startMonthString = this.timeSvc.monthsLocalizedArray(
          startDateParse.month - 1,
          "en"
        );
        let endMonthString = this.timeSvc.monthsLocalizedArray(
          endDateParse.month - 1,
          "en"
        );

        let scheduleExperience: ScheduleExperience = {
          weekDays: schedules[i].weekDays,
          startHour: this.timeSvc.substractString(startDateAsLocalISOTime),
          endHour: this.timeSvc.substractString(endDateAsLocalISOTime),
          startHourIso: schedules[i].startHour,
          endHourIso: schedules[i].endHour,
          startDateNgb: startDateParse,
          endDateNgb: endDateParse,
          startDateDay: startDateParse.day,
          startDateMonth: startMonthString,
          endDateDay: endDateParse.day,
          endDateMonth: endMonthString,
          startDate: startDateParse.day + " " + startMonthString,
          endDate: endDateParse.day + " " + endMonthString,
        };

        Object.keys(LANGUAGE).forEach((key) => {
          this.generalInfoByLang.dataByLang[
            LANGUAGE[key]
          ].scheduleExperience.push(scheduleExperience);
        });
      }
    }
  }

  setType(type: string) {
    if (type) {
      Object.keys(LANGUAGE).forEach((key) => {
        let typeIndex = this.categories.findIndex((item) => item.key === type);

        this.generalInfoByLang.dataByLang[LANGUAGE[key]].typeIndex = typeIndex;
      });
    }
  }

  onNextStep() {
    if (!this.stepsService.isLastStep()) {
      this.stepsService.moveToNextStep();
    }
  }

  onPrevStep() {
    if (!this.stepsService.isFirstStep()) {
      this.stepsService.moveToPrevStep();
    }
  }

  private ConvertDateStringToLocalDateStringIso(date: string) {
    return new Date(
      new Date(date).getTime() - new Date(date).getTimezoneOffset() * 60000
    ).toISOString();
  }

  ngOnDestroy() {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }
}
